import { useInfiniteQuery, useQueries, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getVersionsApi,
  getBrandsForVersionApi,
  getBundlesForBrandVersionApi,
  activateBundleForAllBrandsApi,
  activateBundleForAdminsAllBrandsApi,
  updateBrandConfigApi,
  REFERENCE_BRAND,
} from "../api/bundlesApi";

const VERSIONS_PAGE_SIZE = 10;
const BUNDLES_PAGE_SIZE = 10;

export const useVersionsInfiniteQuery = () => {
  return useInfiniteQuery({
    queryKey: ["versions"],
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      getVersionsApi(pageParam, VERSIONS_PAGE_SIZE),
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < VERSIONS_PAGE_SIZE) {
        return undefined;
      }
      const lastVersion = lastPage[lastPage.length - 1];
      return lastVersion?.version;
    },
  });
};

export const useBrandsQuery = (versionId: string | null) => {
  return useQuery({
    queryKey: ["brands", versionId],
    queryFn: () => getBrandsForVersionApi(versionId!),
    enabled: !!versionId,
  });
};

export const useBrandsForVersionsQuery = (versionIds: string[]) => {
  return useQueries({
    queries: versionIds.map((versionId) => ({
      queryKey: ["brands", versionId],
      queryFn: () => getBrandsForVersionApi(versionId),
    })),
  });
};

export const useBundlesInfiniteQuery = (versionId: string | null, brandId: string | null) => {
  const resolvedBrandId = brandId ?? REFERENCE_BRAND;

  return useInfiniteQuery({
    queryKey: ["bundles", versionId, resolvedBrandId],
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      getBundlesForBrandVersionApi(versionId!, resolvedBrandId, pageParam, BUNDLES_PAGE_SIZE),
    initialPageParam: null,
    enabled: !!versionId,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < BUNDLES_PAGE_SIZE) {
        return undefined;
      }
      const lastBundle = lastPage[lastPage.length - 1];
      return lastBundle?.id;
    },
  });
};

export const useActivateBundleForAllBrandsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionId,
      bundleId,
    }: {
      versionId: string;
      bundleId: string;
    }) => activateBundleForAllBrandsApi(versionId, bundleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      queryClient.invalidateQueries({ queryKey: ["bundles"] });
    },
  });
};

export const useActivateBundleForAdminsAllBrandsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionId,
      bundleId,
    }: {
      versionId: string;
      bundleId: string;
    }) => activateBundleForAdminsAllBrandsApi(versionId, bundleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      queryClient.invalidateQueries({ queryKey: ["bundles"] });
    },
  });
};

export const useUpdateBrandConfigMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      versionId,
      brandId,
      data,
    }: {
      versionId: string;
      brandId: string;
      data: { activeBundleId?: string; activeBundleIdAdmin?: string };
    }) => updateBrandConfigApi(versionId, brandId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      queryClient.invalidateQueries({ queryKey: ["bundles"] });
    },
  });
};
